import { CrossCircledIcon, ExclamationTriangleIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { Box, Callout, Heading, Marker, MdxComponents, mdxComponents as baseMdxComponents, PermanentLink, QuickNav, Separator, Spinner, Text } from '@workos-inc/design-system';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import { UserManagementDiagrams } from './authkit-diagrams';
import { AuthKitStackGrid } from './authkit-stack-grid';
import { CodeBlock } from './code-block';
import { CodeBlockHoverCard } from './code-block-hover-card';
import { CodeBlockReplacement } from './code-block-replacement';
import { CopyMarkdownButton } from './copy-markdown-button';
import { DirectorySyncAttributeSupportTable } from './directory-sync-attribute-support-table';
import { DirectorySyncCsvSchemaTable } from './directory-sync-csv-schema-table';
import { DirectorySyncDiagramsEvents } from './directory-sync-diagrams-events';
import { DirectorySyncDiagramsFoundations } from './directory-sync-diagrams-foundations';
import { DirectorySyncWorkdaySchemaTable } from './directory-sync-workday-schema-table';
import { Event } from './event';
import { ExampleApps } from './example-apps';
import { LanguageSelector } from './language-selector';
import { Link } from './link';
import * as mfaDemos from './mfa-demos';
import { PipesProvidersTable } from './pipes-providers-table';
import { PropsTable } from './props-table';
import { ProviderCards } from './provider-cards';
import { ProviderIconsGrid } from './provider-icons-grid';
import { QuickNavContainer } from './quick-nav-container';
import * as ssoDemos from './sso-demos';
import { WorkOSCliCallout } from './workos-cli-callout';
const CopyMarkdownButtonMdx = () => {
  const router = useRouter();
  // Strip hash fragments and query strings before extracting slug segments
  const pathWithoutHashOrQuery = router.asPath.split(/[?#]/)[0] ?? '';
  const slug = pathWithoutHashOrQuery.split('/').filter(Boolean);
  return <Box my="5" data-sentry-element="Box" data-sentry-component="CopyMarkdownButtonMdx" data-sentry-source-file="mdx-components.tsx">
      <CopyMarkdownButton slug={slug} data-sentry-element="CopyMarkdownButton" data-sentry-source-file="mdx-components.tsx" />
    </Box>;
};
const calloutTypeToColor = {
  warning: 'yellow',
  error: 'red',
  info: 'blue'
} as const;
const calloutTypeToIcon = {
  warning: ExclamationTriangleIcon,
  error: CrossCircledIcon,
  info: InfoCircledIcon
} as const;
const mdxComponents: MdxComponents = {
  ...baseMdxComponents,
  ...mfaDemos,
  ...ssoDemos,
  a: Link,
  AuthKitStackGrid,
  Callout: ({
    children,
    type
  }: {
    children?: React.ReactNode;
    type?: 'warning' | 'error' | 'info';
  }) => {
    const Icon = type ? calloutTypeToIcon[type] : InfoCircledIcon;
    return <Callout.Root color={type ? calloutTypeToColor[type] : 'gray'} my="5">
        <Callout.Icon>
          <Icon height="16" width="16" />
        </Callout.Icon>
        <Text as="div" className="rt-CalloutText" size="2">
          {children}
        </Text>
      </Callout.Root>;
  },
  CodeBlock: props => <Box mb="6" mt="5">
      <CodeBlock {...props} />
    </Box>,
  CodeBlockHoverCard,
  CodeBlockReplacement: ({
    children,
    ...props
  }) => <CodeBlockReplacement {...props} />,
  CopyMarkdownButton: CopyMarkdownButtonMdx,
  DirectorySyncAttributeSupportTable: () => <Box my="5">
      <DirectorySyncAttributeSupportTable />
    </Box>,
  DirectorySyncCsvSchemaTable: {
    Users: () => <Box my="5">
        <DirectorySyncCsvSchemaTable.Users />
      </Box>,
    UserGroups: () => <Box my="5">
        <DirectorySyncCsvSchemaTable.UserGroups />
      </Box>,
    Groups: () => <Box my="5">
        <DirectorySyncCsvSchemaTable.Groups />
      </Box>
  },
  DirectorySyncWorkdaySchemaTable: {
    Users: () => <Box my="5">
        <DirectorySyncWorkdaySchemaTable.Users />
      </Box>,
    Groups: () => <Box my="5">
        <DirectorySyncWorkdaySchemaTable.Groups />
      </Box>
  },
  DirectorySyncDiagram: {
    ...DirectorySyncDiagramsEvents,
    ...DirectorySyncDiagramsFoundations
  },
  ExampleApps,
  GlossaryMarker: props => <Box mt="6" pt={{
    initial: '6',
    sm: '7',
    md: '8'
  }}>
      <Text as="div" mb="5" size="6" weight="bold">
        <Marker {...props} />
      </Text>
      <Separator decorative size="4" />
    </Box>,
  PropsTable: props => <Box my="4">
      <PropsTable {...props} />
    </Box>,
  PipesProvidersTable: () => <Box my="5">
      <PipesProvidersTable />
    </Box>,
  ProviderCards,
  ProviderIconsGrid: props => <ProviderIconsGrid figmaURL="" icons={{
    auth0: {
      slug: 'auth0',
      name: 'Auth0'
    }
  }} {...props} />,
  LanguageSelector: props => <Box mt="5">
      <LanguageSelector {...props} />
    </Box>,
  OktaIntegrationCautionBlock: () => <Callout.Root color="yellow">
      <Text as="div" className="rt-CalloutText" size="2">
        Caution: Okta advises{' '}
        <Link href="https://support.okta.com/help/s/article/Group-Push-Common-Issues?language=en_US" target="_blank">
          using separate groups for push groups and group assignments
        </Link>
        . Otherwise, memberships won't be accurately reflected without manual
        group pushes for membership changes.
      </Text>
    </Callout.Root>,
  PermanentLink: ({
    href = '',
    label = '',
    children,
    ...props
  }: {
    children?: React.ReactNode;
    href?: string;
    label?: string;
    hidden?: boolean;
  }) => <PermanentLink href={href} label={label} {...props}>
      {children}
    </PermanentLink>,
  QuickNav: {
    ...QuickNav,
    Root: props => <QuickNavContainer>
        <QuickNav.Root {...props} />
      </QuickNavContainer>,
    Link: ({
      children,
      href = '',
      ...props
    }: React.ComponentPropsWithoutRef<typeof QuickNav.Link>) => <QuickNav.Link asChild {...props}>
        <NextLink shallow href={href}>
          {children}
        </NextLink>
      </QuickNav.Link>
  },
  WorkOSCliCallout,
  UserManagementDiagrams: {
    AuthenticationMethods: () => <Box my="5">
        <UserManagementDiagrams.AuthenticationMethods />
      </Box>,
    EnterpriseUsers: () => <Box my="5">
        <UserManagementDiagrams.EnterpriseUsers />
      </Box>,
    IdentityLinking: () => <Box my="5">
        <UserManagementDiagrams.IdentityLinking />
      </Box>,
    UserToOrganizationRelationships: () => <Box my="5">
        <UserManagementDiagrams.UserToOrganizationRelationships />
      </Box>
  },
  Spinner,
  DeprecationCallout: {
    MagicLink: () => <Callout.Root color="red" my="5">
        <Callout.Icon>
          <InfoCircledIcon />
        </Callout.Icon>
        <Text as="div" className="rt-CalloutText" size="2">
          <strong>Deprecated API:</strong> Magic Link has been replaced by the
          AuthKit{' '}
          <Link href="/reference/authkit/magic-auth">Magic Auth API</Link>.
        </Text>
      </Callout.Root>
  }
};
const eventsMdxComponents: typeof mdxComponents = {
  ...mdxComponents,
  Event,
  h2: props => <Heading as="h2" mb="3" mt={{
    initial: '8',
    lg: '9'
  }} size="5" {...props} />
};
const referenceMdxComponents: typeof mdxComponents = {
  ...mdxComponents,
  hr: props => <Separator mb="9" mt="7" size="4" {...props} />
};
const releaseMdxComponents: typeof mdxComponents = {
  ...mdxComponents,
  h2: (props: Omit<React.HTMLAttributes<HTMLHeadingElement>, 'color'>) => <Heading as="h4" mb="2" mt="5" size="3" {...props} />,
  h3: (props: Omit<React.HTMLAttributes<HTMLHeadingElement>, 'color'>) => <Heading as="h4" mb="2" mt="5" size="3" {...props} />,
  p: props => <Text as="p" mb="4" size="3" {...props} />,
  ol: props => <Box mb="1" pl="4">
      <ol style={{
      margin: 0,
      padding: 0
    }} {...props} />
    </Box>,
  ul: props => <Box mb="1" pl="4">
      <ul style={{
      margin: 0,
      padding: 0
    }} {...props} />
    </Box>,
  li: props => <Box mb="1">
      <li>
        <Text size="3" {...props} />
      </li>
    </Box>
};
const mdxComponentsBySlug = {
  '*': mdxComponents,
  'events': eventsMdxComponents,
  'reference': referenceMdxComponents
} as const;
export { mdxComponents, mdxComponentsBySlug, releaseMdxComponents };